import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { linear } from '../chart-scales';
import { passSegments, type DistributionRow, type GradeSegment } from '../analytics-charts.model';
import { GRADE_COLORS } from '../chart-palette';
import { useElementWidth } from '../use-element-width';
import { pctText } from './svg-parts';

// b) Barras al 100 % por curso: A–D (+ «sin letra») | F W. Ordenadas por código,
// nunca por tasa. Con n < mínimo, un rótulo en lugar de la barra: ni barra vacía
// ni cero.

export const segmentLabel = (s: GradeSegment) => (s === 'other' ? 'SL' : s);

export function DistributionChart({
  rows,
  minN,
  rateName,
  dCountsInAlert,
}: {
  rows: DistributionRow[];
  minN: number;
  rateName: string;
  dCountsInAlert: boolean;
}) {
  const { t } = useTranslation('sis');
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { ref, width: W } = useElementWidth<HTMLDivElement>();
  const narrow = W < 600;
  const labW = narrow ? 70 : 220;
  const metaW = narrow ? 0 : 170;
  const rowH = narrow ? 52 : 36;
  const barH = 18;
  const x0 = labW;
  const x1 = W - metaW - 4;
  const H = rows.length * rowH + 8;
  const pass = passSegments(dCountsInAlert);
  const ink = theme.palette.text.secondary;
  const strong = theme.palette.text.primary;

  return (
    <div ref={ref}>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={t('reports.analytics.distribution.title')}
      >
        {rows.map((c, r) => {
          const y = 6 + r * rowH;
          const cy = y + barH / 2 + 4;
          const metaX = narrow ? x0 : x1 + 12;
          const metaY = narrow ? y + barH + 24 : cy + 4;
          const label = (
            <>
              <text x={0} y={cy + 4} fontSize={12} fontWeight={600} fill={strong}>
                {c.code}
              </text>
              {!narrow && (
                <text x={70} y={cy + 4} fontSize={12} fill={ink}>
                  {c.name.length > 22 ? `${c.name.slice(0, 21)}…` : c.name}
                  <title>{c.name}</title>
                </text>
              )}
            </>
          );
          if (!c.segments) {
            return (
              <g key={c.courseId}>
                {label}
                <text
                  x={(x0 + x1) / 2}
                  y={cy + 4}
                  fontSize={12}
                  fontStyle="italic"
                  textAnchor="middle"
                  fill={ink}
                >
                  {t('reports.analytics.suppressedLong', { min: minN })}
                </text>
                <text x={metaX} y={metaY} fontSize={11} fill={ink}>
                  {t('reports.analytics.n', { count: c.n })}
                </text>
              </g>
            );
          }
          const segGap = 2;
          const groupGap = 6;
          const shown = c.segments.filter((s) => s.count > 0);
          const usable = x1 - x0 - groupGap - segGap * Math.max(0, shown.length - 1);
          const sc = linear(0, c.n, 0, usable);
          let x = x0;
          let prevPass: boolean | null = null;
          const rects = shown.map((s) => {
            const isPass = pass.has(s.key);
            if (prevPass !== null && prevPass && !isPass) x += groupGap - segGap;
            prevPass = isPass;
            const w = sc(s.count);
            const col = GRADE_COLORS[mode][s.key]!;
            const el = (
              <g key={s.key}>
                <rect x={x} y={y + 4} width={w} height={barH} rx={2} fill={col.fill}>
                  <title>{`${c.code} · ${t(`reports.analytics.distribution.segment.${s.key}`)}: ${s.count}/${c.n} (${pctText(s.share)})`}</title>
                </rect>
                {w >= 22 && (
                  <text
                    x={x + w / 2}
                    y={y + 4 + barH / 2 + 4}
                    fontSize={10.5}
                    fontWeight={600}
                    textAnchor="middle"
                    fill={col.ink}
                    pointerEvents="none"
                  >
                    {segmentLabel(s.key)}
                  </text>
                )}
              </g>
            );
            x += w + segGap;
            return el;
          });
          const alert =
            c.alert.kind === 'value'
              ? `${rateName} ${pctText(c.alert.value)}${c.flag ? ' ▲' : ''}`
              : `${rateName} ${t('reports.analytics.suppressed', { min: minN })}`;
          return (
            <g key={c.courseId}>
              {label}
              {rects}
              <text
                x={metaX}
                y={metaY}
                fontSize={11.5}
                fontWeight={c.flag ? 700 : 400}
                fill={c.flag ? strong : ink}
              >
                {`${alert} · ${t('reports.analytics.n', { count: c.n })}`}
              </text>
              <text
                x={narrow ? metaX + 160 : metaX}
                y={narrow ? metaY : metaY + 13}
                fontSize={10}
                fill={ink}
              >
                {t('reports.analytics.distribution.extra', { aud: c.auditors, legacy: c.legacy })}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
